import { CSSProperties, useEffect, useState, type ChangeEvent } from "react";
import { Alert, Checkbox, FormControlLabel, FormGroup, Stack, Typography } from "@mui/material";

export interface CDSManagedSubdomain {
  id: string;
  domain: string;
  checked: boolean;
  status?: string;
  disabled?: boolean;
}

/**
 * Select shared sending domains while retaining campaign-state and warmup restrictions.
 * Already selected warmup domains remain removable before the campaign starts.
 * @param props Domain selection data, change callback, optional styling, and campaign status.
 * @returns The admin domain checklist without occupancy or domain-capacity restrictions.
 */
export default function CampaignDomainSelection(props: {
  items: CDSManagedSubdomain[];
  onChange: (items: CDSManagedSubdomain[]) => void;
  style?: CSSProperties;
  campaignStatus?: string;
}) {
  const [items, setItems] = useState(props.items);
  useEffect(() => setItems(props.items), [props.items]);

  return (
    <Stack spacing={1} sx={props.style}>
      <Typography variant="subtitle1" fontWeight="bold">Select sending domains</Typography>
      <Typography variant="body2">Each campaign controls its own daily volume per domain. Domains can be shared across campaigns.</Typography>
      <Alert severity="warning">Sending domains cannot be changed once the campaign has started.</Alert>
      {items.length === 0 && <Alert severity="error">At least one sending domain is required.</Alert>}
      <FormGroup>
        {items.map((item: CDSManagedSubdomain) => (
          <FormControlLabel key={item.id} label={item.domain} control={
            <Checkbox checked={item.checked}
              disabled={props.campaignStatus === "running" || props.campaignStatus === "paused" || (item.status === "warming_up" && !item.checked)}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                const updated = items.map((domain: CDSManagedSubdomain) => domain.id === item.id ? { ...domain, checked: event.target.checked } : domain);
                setItems(updated);
                props.onChange(updated);
              }} />
          } />
        ))}
      </FormGroup>
    </Stack>
  );
}
